import { Menu, ArrowLeft, MessageSquare, BarChart3 } from 'lucide-react'
import { useConversationStore } from '../../stores/conversationStore'
import { Button } from '../ui/button'
import { StyleBranching } from './StyleBranching'
import { ViewModeToggle } from './ViewModeToggle'
import { useNavigate, useLocation } from '@tanstack/react-router'
import Logo from '../ui/logo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

type ViewMode = 'split' | 'chat' | 'preview'

interface HeaderProps {
  onToggleSidebar?: () => void
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
  onNavigateBack?: () => void
  pageTitle?: string
}

export function Header({ onToggleSidebar, viewMode, onViewModeChange, onNavigateBack, pageTitle }: HeaderProps) {
  const { activeConversationId, setActiveConversation, conversations } = useConversationStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Get the active conversation data
  const activeConversation = conversations.find(conv => conv.id === activeConversationId)
  const isStyleConversation = activeConversation?.collectionType === 'STYLE'

  const handleLogoClick = () => {
    // Clear active conversation immediately for instant visual feedback
    setActiveConversation(null)

    // Navigate to homepage with optimized approach
    if (location.pathname !== '/') {
      // Coming from a different route - normal navigation
      navigate({
        to: '/',
        search: { slide: undefined }
      })
    } else {
      // Already on homepage - use replace for smoother transition
      navigate({
        to: '/',
        search: { slide: undefined },
        replace: true
      })
    }

    // Ensure conversation stays cleared (reduced delay for snappier feel)
    setTimeout(() => {
      setActiveConversation(null)
    }, 50)
  }

  console.log({ activeConversationId });


  return (
    <header className="flex-shrink-0 bg-transparent py-0.5 px-2">
      <div className="flex items-center justify-between h-10">
        {/* Left Section */}
        <div className="flex items-center gap-2">
          {/* Hamburger Menu Dropdown */}
          {(onToggleSidebar || onNavigateBack) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-white/10 hover:text-primary transition-colors rounded-full"
                  title="Menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background/95 backdrop-blur-xl border-white/10">
                {onNavigateBack && (
                  <>
                    <DropdownMenuItem onClick={onNavigateBack}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Chat
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                  </>
                )}
                {onToggleSidebar && (
                  <DropdownMenuItem onClick={onToggleSidebar}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Conversations
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate({ to: '/analytics' })}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Video Analytics
                </DropdownMenuItem>
                {pageTitle && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuLabel>{pageTitle}</DropdownMenuLabel>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Clickable Logo */}
          <button
            onClick={handleLogoClick}
            className="hover:scale-105 active:scale-95 transition-all duration-150 cursor-pointer ml-1"
            title="Go to Hero page"
          >
            <Logo variant="modern" className="h-8" />
          </button>

          {/* Style branch switcher — only for style conversations.
              (The per-conversation BranchSwitcher was deprecated: it fetched git
              branches for every project, 404-ing on the majority that have no
              session.) */}
          {activeConversationId && isStyleConversation && (
            <div className="ml-4">
              <StyleBranching
                sessionId={activeConversationId} // For now, use conversationId as sessionId
                onBranchSwitch={(branch) => {
                  console.log(`Switched to style branch: ${branch}`);
                  // Force a page reload to ensure all cached content is refreshed
                  // This is a temporary aggressive solution until proper cache invalidation is fully implemented
                  window.location.reload();
                }}
              />
            </div>
          )}
        </div>

        {/* Right Section - View Mode Toggle Buttons */}
        {activeConversationId && onViewModeChange && viewMode && (
          <div className="bg-background/50 backdrop-blur-md rounded-full p-1 border border-white/10 shadow-sm">
            <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
          </div>
        )}
      </div>
    </header>
  )
}